import { useState, useEffect, useRef, useCallback } from 'react';

interface NavItem {
  name: string;
  href: string;
  icon: any;
}

export function useOverflowNavigation(items: NavItem[], moreButtonWidth: number = 100) {
  const containerRef = useRef<HTMLDivElement>(null);
  const itemWidthsRef = useRef<Map<number, number>>(new Map());
  const [visibleCount, setVisibleCount] = useState(items.length);

  const calculateVisibleItems = useCallback(() => {
    if (!containerRef.current || itemWidthsRef.current.size === 0) return;
    
    const containerWidth = containerRef.current.offsetWidth;
    let totalWidth = 0;
    let count = 0;

    for (let i = 0; i < items.length; i++) {
      const itemWidth = itemWidthsRef.current.get(i) || 0;
      const remainingItems = items.length - i - 1;
      
      // If there are remaining items, reserve space for the More button
      const needsMoreButton = remainingItems > 0 && totalWidth + itemWidth + moreButtonWidth > containerWidth;
      
      if (needsMoreButton) {
        break;
      }
      
      if (totalWidth + itemWidth <= containerWidth) {
        totalWidth += itemWidth;
        count++;
      } else {
        break;
      }
    }

    setVisibleCount(Math.max(1, count)); // Always show at least 1
  }, [items.length, moreButtonWidth]);

  // ResizeObserver to watch container
  useEffect(() => {
    if (!containerRef.current) return;
    
    const resizeObserver = new ResizeObserver(() => {
      calculateVisibleItems();
    });
    
    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, [calculateVisibleItems]);

  // Recalculate when item widths change
  useEffect(() => {
    if (itemWidthsRef.current.size === items.length) {
      calculateVisibleItems();
    }
  }, [items.length, calculateVisibleItems]);

  const measureItem = useCallback((index: number, width: number) => {
    itemWidthsRef.current.set(index, width);
    if (itemWidthsRef.current.size === items.length) {
      calculateVisibleItems();
    }
  }, [items.length, calculateVisibleItems]);

  return {
    containerRef,
    visibleItems: items.slice(0, visibleCount),
    overflowItems: items.slice(visibleCount),
    measureItem,
  };
}
